import { useEffect, useMemo, useState } from 'react';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useTrainingExercises } from '../../../hooks/useTrainingExercises';
import { useGetExerciseOptions } from '../../../redux/api/exerciseConfigAPI';
import {
  useDeleteTraining,
  useGetTrainingById,
} from '../../../redux/api/trainingsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { TrainingStatus } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { ITraining } from '../../../types/interfaces/training';
import { groupBy } from '../../../utils/collections/array-utils';
import { orderTraining } from '../../../utils/training/set-utils';
import { isTrainingSharedWithUser } from '../../../utils/training/training-share-utils';

export function useViewTrainingPage(trainingId?: string) {
  const { navigate, back } = useNavigationStack();
  const {
    data: training,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTrainingById(trainingId);
  const { exercises, isLoading: isTrainingExercisesLoading } =
    useTrainingExercises(training?.id);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [deleteTrainingApi, { isLoading: isDeletingTraining }] =
    useDeleteTraining();
  const { data: user } = useGetMyDetails();
  const { data: exerciseOptions } = useGetExerciseOptions();

  const orderedTraining: ITraining = useMemo(() => {
    if (
      !training ||
      !exercises ||
      !exerciseOptions ||
      isTrainingExercisesLoading
    ) {
      return null;
    }

    return orderTraining(training, exercises, exerciseOptions);
  }, [training, exercises, exerciseOptions, isTrainingExercisesLoading]);

  useEffect(() => {
    if (!training || !user?.id) {
      return;
    }

    const sharedWithMe = isTrainingSharedWithUser(training, user.id);

    if (
      training.status === TrainingStatus.started &&
      training.permissions?.canOpenLikeCurrent
    ) {
      navigate(Paths.trainings.toCurrent(trainingId), { replace: true });
      return;
    }

    if (training.status === TrainingStatus.finished) {
      navigate(Paths.trainings.toFinished(trainingId), { replace: true });
    }
  }, [training, user?.id, trainingId, navigate]);

  const trainingSetGroups = useMemo(
    () =>
      orderedTraining?.trainingSets
        ? groupBy(orderedTraining?.trainingSets, 'setNumber')
        : null,
    [orderedTraining],
  );

  function deleteTraining() {
    deleteTrainingApi({ id: trainingId })
      .unwrap()
      .then(() => {
        setShowDeleteConfirmation(false);
        back();
      })
      .catch(console.error);
  }

  return {
    training,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    trainingSetGroups,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleteTraining,
    isDeletingTraining,
  };
}
